import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { ISuccessSingleResponse } from '@shared/models/success-single-response';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import {
  IFundRequest,
  IFund,
  IFundsResponse,
  IFundDetailRequest,
  IFundDetail,
} from '../models/fund';

@Injectable({
  providedIn: 'root',
})
export class FundService {
  private baseUrl: string = environment.SMART_API_URL;
  private readonly http: HttpClient = inject(HttpClient);

  private fundSubject: BehaviorSubject<null> = new BehaviorSubject<null>(null);
  public fund$: Observable<null> = this.fundSubject.asObservable();

  getFunds(): Observable<ISuccessSingleResponse<IFundsResponse>> {
    const getFundsUrl: string = `${this.baseUrl}funds-demostrative/public/all`;
    const response: Observable<ISuccessSingleResponse<IFundsResponse>> =
      this.http.get<ISuccessSingleResponse<IFundsResponse>>(getFundsUrl);

    return response;
  }

  getPublicFunds(): Observable<ISuccessSingleResponse<IFundsResponse>> {
    const getPublicFundsUrl: string = `${this.baseUrl}funds-demostrative/public`;
    const response: Observable<ISuccessSingleResponse<IFundsResponse>> =
      this.http.get<ISuccessSingleResponse<IFundsResponse>>(getPublicFundsUrl);

    return response;
  }

  createFund(fund: IFundRequest): Observable<ISuccessSingleResponse<IFund>> {
    const createFundUrl: string = `${this.baseUrl}funds-demostrative`;
    const response: Observable<ISuccessSingleResponse<IFund>> = this.http
      .post<ISuccessSingleResponse<IFund>>(createFundUrl, fund)
      .pipe(tap(() => this.fundSubject.next(null)));

    return response;
  }

  createFundV2(fundData: FormData): Observable<ISuccessSingleResponse<IFund>> {
    const createFundV2Url: string = `${this.baseUrl}funds-demostrative/v2`;
    const response: Observable<ISuccessSingleResponse<IFund>> = this.http
      .post<ISuccessSingleResponse<IFund>>(createFundV2Url, fundData)
      .pipe(tap(() => this.fundSubject.next(null)));

    return response;
  }

  createDetail(
    detail: IFundDetailRequest,
  ): Observable<ISuccessSingleResponse<IFundDetail>> {
    const createDetailUrl: string = `${this.baseUrl}funds-demostrative-details`;
    const response: Observable<ISuccessSingleResponse<IFundDetail>> = this.http
      .post<ISuccessSingleResponse<IFundDetail>>(createDetailUrl, detail)
      .pipe(tap(() => this.fundSubject.next(null)));

    return response;
  }

  updateDetail(
    detailId: number,
    detail: IFundDetailRequest,
  ): Observable<ISuccessSingleResponse<IFundDetail>> {
    const updateDetailUrl: string = `${this.baseUrl}funds-demostrative-details/${detailId}`;
    const response: Observable<ISuccessSingleResponse<IFundDetail>> = this.http
      .put<ISuccessSingleResponse<IFundDetail>>(updateDetailUrl, detail)
      .pipe(tap(() => this.fundSubject.next(null)));

    return response;
  }

  deleteDetail(
    detailId: number,
  ): Observable<ISuccessSingleResponse<IFundDetail>> {
    const deleteDetailUrl: string = `${this.baseUrl}funds-demostrative-details/${detailId}`;
    const response: Observable<ISuccessSingleResponse<IFundDetail>> = this.http
      .delete<ISuccessSingleResponse<IFundDetail>>(deleteDetailUrl)
      .pipe(tap(() => this.fundSubject.next(null)));

    return response;
  }

  updateFundStatus(
    fundId: number,
    isActive: boolean,
  ): Observable<ISuccessSingleResponse<IFund>> {
    const updateStatusUrl: string = `${this.baseUrl}funds-demostrative/${fundId}/status`;
    const response: Observable<ISuccessSingleResponse<IFund>> = this.http
      .patch<
        ISuccessSingleResponse<IFund>
      >(updateStatusUrl, { active: isActive })
      .pipe(tap(() => this.fundSubject.next(null)));

    return response;
  }

  updateFund(
    fundId: number,
    fundData: Partial<IFundRequest>,
  ): Observable<ISuccessSingleResponse<IFund>> {
    const updateFundUrl: string = `${this.baseUrl}funds-demostrative/${fundId}`;
    const response: Observable<ISuccessSingleResponse<IFund>> = this.http
      .put<ISuccessSingleResponse<IFund>>(updateFundUrl, fundData)
      .pipe(tap(() => this.fundSubject.next(null)));

    return response;
  }

  updateFundV2(
    fundId: number,
    fundData: FormData,
  ): Observable<ISuccessSingleResponse<IFund>> {
    const updateFundV2Url: string = `${this.baseUrl}funds-demostrative/v2/${fundId}`;
    const response: Observable<ISuccessSingleResponse<IFund>> = this.http
      .put<ISuccessSingleResponse<IFund>>(updateFundV2Url, fundData)
      .pipe(tap(() => this.fundSubject.next(null)));

    return response;
  }

  downloadFile(idFund: string): Observable<Blob> {
    const downloadUrl: string = `${this.baseUrl}reports/fund-portfolio-buffer`;
    const payload = { idFund };
    const response = this.http.post(downloadUrl, payload, {
      responseType: 'blob',
    });

    return response;
  }
}
